import { Head, Link } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"

type AuditRow = {
  id: number
  token: string
  url: string
  email: string
  business_name: string | null
  status: string
  grade: string | null
  created_at: string
}

export default function AdminSeoAuditsIndex({ audits }: { audits: AuditRow[] }) {
  return (
    <>
      <Head title="SEO Audits">
        <meta name="description" content="SEO audit leads captured by the free audit tool." />
        <meta property="og:title" content="SEO Audits" />
        <meta property="og:description" content="SEO audit leads captured by the free audit tool." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <h1>SEO Audits</h1>
          <p className="mt-1">
            {audits.length} {audits.length === 1 ? "audit" : "audits"} run via the
            free audit tool. Each one is a lead.
          </p>
        </div>

        {audits.length === 0 ? (
          <p className="mt-10 text-ink-muted">No audits yet.</p>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-md border border-hairline">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-surface text-left">
                  <th className="px-4 py-3 font-medium text-ink-muted">Site</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Email</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Grade</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Status</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Ran</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {audits.map((a) => (
                  <tr key={a.id} className="hover:bg-surface">
                    <td className="px-4 py-3">
                      <Link
                        href={`/admin/seo_audits/${a.id}`}
                        className="font-medium text-ink-display no-underline hover:text-accent-display"
                      >
                        {a.url}
                      </Link>
                      {a.business_name && <span className="block text-xs text-ink-muted">{a.business_name}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <a href={`mailto:${a.email}`} className="text-ink-body no-underline hover:text-accent-display">
                        {a.email}
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      {a.grade ? <GradePill grade={a.grade} /> : <span className="text-ink-muted">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={a.status} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-ink-muted">{formatDate(a.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminShell>
    </>
  )
}

function GradePill({ grade }: { grade: string }) {
  const tone = grade === "A" || grade === "B" ? "signal" : grade === "C" ? "muted" : "accent"
  return <Badge tone={tone as "signal" | "muted" | "accent"}>{grade}</Badge>
}

function StatusBadge({ status }: { status: string }) {
  if (status === "complete") return <Badge tone="signal">Complete</Badge>
  if (status === "failed") return <Badge tone="accent">Failed</Badge>
  return <Badge tone="muted">{status === "running" ? "Running" : "Pending"}</Badge>
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
